import { useRef } from "react";
import {
  createWatchTracker,
  WATCH_COMPLETION_RATIO,
  type WatchTracker,
} from "./watchTrackerLogic";

/**
 * Tracks unique whole-seconds the user has actually watched and fires
 * `onComplete` once a real-watch threshold is reached. The threshold adapts
 * to the clip length (see `getRatioForDuration`): 50% for clips up to 30s,
 * 60% for anything longer.
 *
 * Used by all the challenge video players (native MP4, YouTube IFrame API,
 * Vimeo iframe SDK, Livid player.js) to keep completion semantics consistent
 * and resistant to seek-skipping. The rules themselves live in
 * `createWatchTracker` (watchTrackerLogic.ts); this hook only gives one
 * tracker a component lifetime.
 *
 * Callers pass a fresh time/duration sample on every player tick:
 *   `record(currentTime, duration)`
 *
 * For player-reported "ended" events, call `checkEnded(duration)`. This honors
 * completion at the relaxed WATCH_ENDED_RATIO so legitimate full watches that
 * lost timeupdate samples still complete — while pure seek-to-end (which
 * accumulates 0 unique seconds) is still rejected.
 *
 * For looping players (YouTube Shorts auto-loop, Vimeo loop=1) call
 * `recordWithLoopDetection(currentTime, duration)` instead of `record`. It
 * detects a backward jump in currentTime past LOOP_BOUNDARY_RATIO and treats
 * it as a "full pass watched" → triggers checkEnded automatically.
 */
export function useWatchTracker(
  onComplete?: () => void,
  ratio?: number,
): WatchTracker {
  // Stash the latest callback and ratio so the tracker (and the functions on
  // it) stays referentially stable across re-renders — consumers put it into
  // useEffect deps.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const ratioRef = useRef(ratio);
  ratioRef.current = ratio;

  const trackerRef = useRef<WatchTracker | null>(null);
  if (!trackerRef.current) {
    trackerRef.current = createWatchTracker(
      () => onCompleteRef.current?.(),
      () => ratioRef.current,
    );
  }
  return trackerRef.current;
}

// Re-export so existing imports of WATCH_COMPLETION_RATIO from this module
// keep working.
export { WATCH_COMPLETION_RATIO };
